import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  Inject,
  Input,
  OnDestroy,
  Renderer2,
} from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { AuthenticationService, formatCountdown } from 'ngx-core-services';
import { Store } from '@ngrx/store';
import { Observable, Subject, combineLatest } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';

import { CreateBlockReduxSupport, IReduxBlock } from '../../store/helpers/decorator';
import { SystemsState } from '../../store';
import { setRealTimeTelemetryOverlayHidden } from '../../store/actions/real-time.actions';
import { RtBaseComponent } from '../rt-base/rt-base.component';

const getLabels = () => ({
  hide: 'Hide',
  ended: 'ENDED',
  endedAt: 'Ended at',
  endsIn: 'Ends in',
  idle: 'IDLE',
  live: 'LIVE',
  restart: 'Restart RT',
  start: 'Start RT',
  stop: 'Stop RT',
  stopping: 'STOPPING',
  title: 'Real-Time Telemetry',
  dailyState: {
    title: '',
    loading: 'Loading ...',
    chargedEnergy: 'Daily charged energy',
    consumedEnergy: 'Daily consumed energy',
    dischargedEnergy: 'Daily discharged energy',
    exportedEnergy: 'Daily exported energy',
    generatedEnergy: 'Daily generated energy',
    importedEnergy: 'Daily imported energy',
    energyDailyIrradiation: 'Daily irradiation',
    noDataForTheSelectedPeriod: 'No data for the selected period',
    P_DC_TOT: 'Power generating',
    P_B_AGGR: 'Power charging',
    P_B_AGGR_POS: 'Power charging',
    P_B_AGGR_NEG: 'Power discharging',
    P_EXP: 'Power exporting',
    P_EXP_POS: 'Power exporting',
    P_EXP_NEG: 'Power importing',
    P_LOAD: 'Power consuming',
    SOL_IRR: 'Irradiance',
    SOL_TEMP: 'Temperature',
  },
  flowChart: {
    title: '',
    home: 'Home',
    grid: 'Grid',
    solar: 'Solar',
    battery: 'Battery',
    loading: 'Loading ...',
    disconnected: 'Disconnected',
    unit: 'Unit',
    irradiance: 'Irradiance',
    panelTemp: 'Temperature',
  },
});

@CreateBlockReduxSupport()
@Component({
  selector: 'lib-rt-overlay',
  templateUrl: './rt-overlay.component.html',
  styleUrls: ['./rt-overlay.component.scss'],
})
export class RtOverlayComponent extends RtBaseComponent implements IReduxBlock, AfterViewInit, OnDestroy {
  readonly _labels = getLabels();
  @Input() set labels(value: Partial<typeof this._labels>) {
    if (!value) {
      return;
    }
    const { dailyState, flowChart, ...rest } = value;
    Object.assign(this._labels, rest);
    if (dailyState) {
      Object.assign(this._labels.dailyState, dailyState);
    }
    if (flowChart) {
      Object.assign(this._labels.flowChart, flowChart);
    }
  }
  get labels(): typeof this._labels {
    return this._labels;
  }

  @Input() override refresh$ = new Subject<void>();
  override _refresh$ = new Observable<boolean>();

  readonly _instanceId$: Observable<string> = new Observable<string>();
  private mountedToBody = false;

  readonly rtOverlayState$ = combineLatest([
    this.rtSession$,
    this.canUseRealTime$,
    this.userCanUseRealTime$,
    this.rtTick$,
  ]).pipe(
    map(([session, canUseRealTime, userCanUseRealTime]) => {
      const timing = this.getRTSessionTiming(session);

      const effectiveCanUseRealTime = Boolean(canUseRealTime && userCanUseRealTime);

      return {
        canUseRealTime: effectiveCanUseRealTime,
        countdownSeconds: timing.countdownSeconds,
        endedAtLabel: session.endedAt ? new Date(session.endedAt).toLocaleTimeString() : null,
        hasJustEnded: !session.active && Boolean(session.endedAt),
        isActive: Boolean(session.active),
        isStopping: timing.isStopping,
        rtProgress: timing.rtProgress,
        visible: Boolean(userCanUseRealTime && session.active && session.overlayRequestedByUser && !session.overlayHidden),
      };
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  constructor(
    override readonly store: Store<SystemsState>,
    readonly el: ElementRef,
    readonly renderer: Renderer2,
    authenticationService: AuthenticationService,
    @Inject(DOCUMENT) private readonly document: Document,
  ) {
    super(store, authenticationService);
  }

  ngAfterViewInit(): void {
    const hostElement = this.el.nativeElement as HTMLElement;
    if (!this.mountedToBody && hostElement?.parentElement !== this.document.body) {
      this.renderer.appendChild(this.document.body, hostElement);
      this.mountedToBody = true;
    }
  }

  ngOnDestroy(): void {
    const hostElement = this.el.nativeElement as HTMLElement;
    if (this.mountedToBody && hostElement?.parentElement === this.document.body) {
      this.renderer.removeChild(this.document.body, hostElement);
      this.mountedToBody = false;
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.onClose();
  }

  onClose(): void {
    const systemId = this.system$.value?.uid;
    if (!systemId) {
      return;
    }

    this.store.dispatch(setRealTimeTelemetryOverlayHidden({ systemId, hidden: true }));
  }

  formatCountdown(totalSeconds: number | null): string {
    return formatCountdown(totalSeconds);
  }

  onBackdropClick(event: MouseEvent): void {
    if ((event.target as HTMLElement).classList.contains('rt-overlay')) {
      this.onClose();
    }
  }
}
